import { Button, Heading, Modal, ModalProps, Text } from "@ttc3k/trekker";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "react-responsive";
import { Box, Divider, Flex } from "styled-system/jsx";
import { useBookerContext } from "widgets/booker/contexts";

/** The footer fades content out above the button; content is padded so its end can clear the fade. */
const FOOTER_FADE_HEIGHT = 60;

type OverlayPageProps = {
  title: string;
  subtitle?: string;
  open: boolean;
  onClose: () => void;
  buttonLabel?: string;
  buttonDisabled?: boolean;
  onButtonClick?: () => void;
};

export type ConsumingOverlayPageProps = Omit<
  OverlayPageProps,
  "title" | "subtitle" | "buttonLabel" | "buttonDisabled" | "onButtonClick"
>;

export const OverlayPage = ({
  open,
  title,
  subtitle,
  children,
  onClose,
  buttonLabel,
  buttonDisabled,
  onButtonClick,
  ...rest
}: OverlayPageProps & Omit<ModalProps, "onOpenChange" | "open">) => {
  const { bookerPosition } = useBookerContext();
  // Panda responsive design syntax isn't working for overlay
  const isDevice = useMediaQuery({ maxWidth: 1024 });

  const { t } = useTranslation("booker", { keyPrefix: "OVERLAY_PAGE" });
  return (
    <Modal
      open={open}
      placement={bookerPosition === "left" ? "leftPane" : "rightPane"}
      backdropProps={{
        bg: "transparent",
      }}
      hideCloseTrigger
      portal
      {...rest}
    >
      <Box
        bg={"ink.100"}
        minH={"full"}
        h={"auto"}
        w={"full"}
        px={"400"}
        pt={"800"}
        display={"flex"}
        flexDir={"column"}
        justifyContent={"space-between"}
        position={"relative"}
      >
        <Box pb={`${FOOTER_FADE_HEIGHT}px`}>
          <Flex
            alignItems={"center"}
            justifyContent={"space-between"}
            gap={"100"}
          >
            <Heading size={"h4"}>{title}</Heading>
            <Button mode={"light"} onClick={onClose} type="button">
              {t("BACK")}
            </Button>
          </Flex>
          <Divider my={"250"} h={"2px"} bg={"border.dark"} />
          {subtitle && <Text color={"text.mid"}>{subtitle}</Text>}
          {children}
        </Box>
        <Box
          position={"sticky"}
          bottom={0}
          left={0}
          boxSizing={"border-box"}
          width={"full"}
          mt={isDevice ? "500" : "150"}
          pb={
            isDevice
              ? "calc(token(spacing.400) + env(safe-area-inset-bottom, 0px))"
              : "500"
          }
          bg={"ink.100"}
        >
          <Box
            height={`${FOOTER_FADE_HEIGHT}px`}
            position={"absolute"}
            top={`-${FOOTER_FADE_HEIGHT}px`}
            left={0}
            width={"full"}
            background={
              "linear-gradient(0deg, token(colors.ink.100) 0%, rgba(250, 247, 243, 0.00) 100%)"
            }
            pointerEvents={"none"}
          />
          <Button
            width={"full"}
            size={"lg"}
            disabled={buttonDisabled}
            onClick={onButtonClick}
            type="button"
          >
            {buttonLabel ?? t("DONE")}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};
